import { Injectable, inject, signal } from '@angular/core';
import {
  DEFAULT_AUTH_REFRESH_TIMEOUT_MS,
  SPINOFF_AUTH_CONFIG,
  SpinoffAuthService,
} from '@sbe-spinoff-client/spinoff-platform';
import { LegacyHandoffService } from './legacy-handoff.service';

/**
 * `libs/client/spinoff-platform`의 `SpinoffBootGateService`(sms/timetable/commerce가
 * 쓰는 제네릭 버전)를 이 앱 전용으로 확장한 것 — 그 서비스는 `auth.init()` 한 번만
 * 기다리는데, 이 앱은 그 뒤에 레거시(sbe-web-v4) 세션 계승(`LegacyHandoffService.tryAdopt()`
 * → 성공 시 `auth.init()` 재호출)까지 부팅 시퀀스에 포함돼 있다(과거
 * `provideAppInitializer`가 부트스트랩 자체를 블로킹하며 순서를 보장했던 것과 동일한
 * 시퀀스, app.config.ts 참고). 제네릭 `SpinoffBootGateService`를 그대로 쓰면
 * `auth.init()`의 in-flight promise 캐시 때문에 그 서비스의 `completed`가 계승 완료 전에
 * 먼저 true가 돼, 가드가 계승 중인 사용자를 미인증으로 오판해 `/login`으로 튕겨낼 수 있다
 * (2026-09-24, 콜드스타트 로딩 UI 도입 검토 중 확인).
 *
 * `ensureStarted()`/`whenReady()` 둘 다 같은 in-flight promise를 공유해 중복 호출해도
 * 시퀀스가 두 번 돌지 않는다 — 가드(`authGuard`/`redirectIfLoggedInGuard`)와
 * `AuthorizeComponent`는 `whenReady()`로 완료를 기다리고, 앱 루트(`app.ts`)는
 * `ensureStarted()`로 부팅 시점에 한 번 트리거만 한다.
 */
@Injectable({ providedIn: 'root' })
export class BootGateService {
  private readonly auth = inject(SpinoffAuthService);
  private readonly legacyHandoff = inject(LegacyHandoffService);
  private readonly config = inject(SPINOFF_AUTH_CONFIG);
  private readonly _completed = signal(false);
  private initPromise: Promise<void> | null = null;

  readonly completed = this._completed.asReadonly();

  ensureStarted(): void {
    void this.whenReady();
  }

  whenReady(): Promise<void> {
    if (this._completed()) return Promise.resolve();
    if (this.initPromise) return this.initPromise;

    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutMs = this.config.refreshTimeoutMs ?? DEFAULT_AUTH_REFRESH_TIMEOUT_MS;
    const timeout = new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, timeoutMs);
    });
    const run = (async () => {
      try {
        await this.auth.init();
        if (this.auth.isAuthenticated()) return;
        const adopted = await this.legacyHandoff.tryAdopt();
        if (adopted) {
          await this.auth.init();
        }
      } catch (err) {
        // auth.init()/tryAdopt() 둘 다 현재 구현상 reject하지 않지만(각 서비스 내부에서
        // 자체적으로 흡수), 그 보장은 이 클래스 밖의 코드에 있다 — 여기서도 방어적으로
        // 흡수해야 한다. whenReady()를 직접 await하는 authGuard/redirectIfLoggedInGuard가
        // CanActivateFn이라, 이 promise가 reject되면 Angular Router가 내비게이션 자체를
        // 실패 처리해 화면이 먹통이 된다(Gemini 리뷰 지적, 2026-09-24) — 실패해도 미인증
        // 상태로 간주하고 계속 진행하는 편이 안전하다.
        console.error('[BootGateService] 부팅 시퀀스 실패 — 미인증으로 간주하고 진행합니다.', err);
      }
    })();

    this.initPromise = Promise.race([run, timeout]).finally(() => {
      clearTimeout(timeoutId);
      this._completed.set(true);
    });
    return this.initPromise;
  }
}
